import React, { useEffect, useState, useContext } from "react";
import ActionsContext from "common/ActionsContext";
import Modal from "common/Modal.jsx";
import TextInput from "common/TextInput";
import SelectInput from "common/SelectInput";
import {parseSystemSecurity, securityIsStrict} from "common/api";
import SelectAutocomplete from "common/SelectAutocomplete";
import BqnContext from "common/BqnContext";
import styled from "styled-components";

const AskUserName = () => (
  <TextInput
    type="text"
    name="username"
    label="Username"
    className="input-control"
    icon="person"
  />
);

const AskDescription = () => (
  <TextInput
    type="text"
    name="description"
    label="Description"
    className="input-control"
    icon="description"
  />
);

const profileOptions = [
  { value: "administrator", label: "BQN Administrator" },
  { value: "operator", label: "BQN Operator" },
];

const AskProfile = () => (
  <SelectInput
    type="text"
    name="profile"
    label="Profile"
    className="input-control"
    icon="group"
    options={profileOptions}
    defaultValue="administrator"
  />
);

const WrapperSelect = styled.div`
  display: flex;
  align-items: center;
  margin: 0px 7px 15px 7px;
`;

const SelectTitle = styled.div`
  margin-right: 20px;
  font-family: "AllianceNo2-SemiBold" !important;
  font-size: 13px;
`;

const AskBqn = ({ value, onChange, options }) => {
  const selectedOption = options.find(opt => opt.value == value);

  return (
  <WrapperSelect>
    <SelectTitle>Select BQN</SelectTitle>
    <SelectAutocomplete
      title="bqn-select"
      name="bqn-select"
      selected={selectedOption}
      onChange={(value) => {
        onChange(value);
      }}
      options={options}
      isOptionEqualToValue={(option, value) => option.value.toString() === value.toString()}
    />
  </WrapperSelect>
)}

const invalid = (reason) => {
  throw reason;
};

const notAllowedUsernames = new Set(['admin', 'administrator', 'administration','root', 'system']);
const validStrictUsername = (username = '') => {
  if (notAllowedUsernames.has(username.toLowerCase())) {
    throw `${username} is not an allowed username`
  }
  if (username.length < 6) {
    throw 'Minimun user name length is 6 characters';
  }
  if (username.length > 16) {
    throw 'Maximum user name length is 16 characters';
  }
  return nonCapitalUsername(username);
}

const nonCapitalUsername = username => {
  if (!/^[a-z_][a-z0-9_-]*$/.test(username)) {
    throw `Usernames must start with a lower case letter or an underscore, followed by lower case letters, digits, underscores, or dashes.`
  }
  return username;
}

const loadStrictSecurity = () =>
  ifCl.run('show system security').then(parseSystemSecurity).then(securityIsStrict)
    .catch( error => {
      if (error === 'Strict security not enabled') {
        return false; /*not enabled*/
      }
      throw error;
    });

const validate = ({ username = "", ...rest }) =>
  username.length < 6
    ? invalid("Minimun user name length is 6 characters")
    : loadStrictSecurity().then( strict => 
      ({
        username: strict ? validStrictUsername(username): nonCapitalUsername(username),
        ...rest
      })
    ) 

const composeCommand = ({ username, description, profile }, mode, bqnId) =>
  `system users add "${username}" "${description}" ${profile} ${
    mode === "cluster" ? `bqn-id ${bqnId}` : ""
  }`;

const AddUserModal = () => {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(null);
  
  const actions = useContext(ActionsContext);
  const {
    bqns,
    loginBqnId,
    bqnId,
    clusterStatus: { mode },
  } = useContext(BqnContext);
  
  const [bqnSelected, setBqnSelected] = useState();

  const options = bqns.map(({ bqnId }) => {
    return { label: bqnId, value: bqnId };
  });

  const whenDone = () => actions.send("do-load");

  const doOpen = () => {
    setOpen(true);
  };
  const doClose = () => {
    setOpen(false);
  };
  const doApply = (settings) =>
    Promise.resolve(settings)
      .then(validate)
      .then((settings) => ifCl.run(composeCommand(settings, mode, bqnSelected)))
      .then(whenDone)
      .then(doClose)
      .catch(setError);

  useEffect(() => actions.recv(`add-user`, () => doOpen()), []);

  useEffect(() => {
    setBqnSelected(bqnId === "all" ? loginBqnId : bqnId)
  }, [bqnId])

  return open !== true ? null : (
    <Modal
      title="Add User"
      superIcon="person_add"
      onApply={doApply}
      onClose={doClose}
      closeLabel="CANCEL"
      content={() => (
        <>
          {mode === "cluster" && (
            <AskBqn
              options={options}
              value={bqnSelected}
              onChange={setBqnSelected}
            />
          )}
          <AskUserName />
          <AskDescription />
          <AskProfile />
        </>
      )}
      footerMessage={
        (error !== null && (
          <span className="modal-err-msg color-red align-left">
            {console.error(error) || String(error)}
          </span>
        )) ||
        null
      }
    />
  );
};

export default AddUserModal;
